import { useCallback, useId, useState } from 'react';

/**
 * Campo de texto estilo ann `TextField`: label flotante sobre el borde, estados (default, error,
 * alert, success, disabled), tamaños small/medium, texto de apoyo e íconos (Lucide).
 *
 * **Controlado:** si pasas `value`, usa siempre string (`value ?? ''`) para evitar avisos de React.
 * **Deshabilitado:** usa la prop nativa **`disabled`** o **`state="disabled"`** (equivalente); no
 * mezcles criterios sin necesidad.
 *
 * @param {'default'|'error'|'alert'|'success'|'disabled'} [props.state='default']
 * @param {'small'|'medium'} [props.size='medium']
 * @param {import('react').ChangeEventHandler<HTMLInputElement>} [props.onInputChange] — handler nativo del input (opcional).
 * @param {(value: string) => void} [props.onChange] — como ann: recibe el string (`onChange(value)`).
 */
const Textfield = ({
  value,
  defaultValue = '',
  state = 'default',
  size = 'medium',
  label = '',
  placeholder = 'Placeholder',
  supportingText = '',
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  onChange,
  onInputChange,
  className = '',
  id: idProp,
  disabled: disabledProp,
  ...inputProps
}) => {
  const {
    className: inputClassName = '',
    style: inputStyle,
    type: inputType,
    ...restInput
  } = inputProps;

  const reactId = useId();
  const supportId = useId();
  const inputId = idProp ?? `textfield-${reactId}`;
  const disabled = disabledProp ?? state === 'disabled';

  const isControlled = value !== undefined;
  const [internal, setInternal] = useState(defaultValue);

  const displayValue = isControlled ? (value ?? '') : internal;

  const setValue = useCallback(
    (next) => {
      if (!isControlled) setInternal(next);
    },
    [isControlled]
  );

  const handleChange = useCallback(
    (e) => {
      onInputChange?.(e);
      const v = e.target.value;
      setValue(v);
      onChange?.(v);
    },
    [onChange, onInputChange, setValue]
  );

  const labelSizeClass = size === 'small' ? 'text-[12px] leading-[1.2]' : 'text-[14px] leading-[1.2]';
  const inputSizeClass = size === 'small' ? 'text-[12px]' : 'text-[14px]';
  const heightClass = size === 'small' ? 'h-10' : 'h-[58px]';
  const iconSize = size === 'small' ? 16 : 20;

  const describedBy = supportingText ? supportId : undefined;

  const labelTone =
    disabled || state === 'disabled' ? 'text-content-faint' : 'text-content';

  const supportingTone = (() => {
    if (disabled || state === 'disabled') return 'text-content-faint';
    if (state === 'error') return 'text-red-700 dark:text-red-300';
    if (state === 'success') return 'text-emerald-800 dark:text-emerald-300';
    if (state === 'alert') return 'text-amber-900 dark:text-amber-200';
    return 'text-content-secondary';
  })();

  /**
   * Bordes: tokens `edge` / semánticos + pares `dark:` (surface del shell).
   */
  const wrapperBorderClasses = (() => {
    if (disabled) {
      return 'border border-solid border-edge opacity-70 dark:border-gray-600';
    }
    if (state === 'default') {
      return [
        'border border-solid border-edge',
        'hover:border-edge-strong dark:hover:border-gray-500',
        'has-[:focus-visible]:border-2 has-[:focus-visible]:border-content-secondary',
        'dark:has-[:focus-visible]:border-gray-400',
      ].join(' ');
    }
    if (state === 'error') {
      return [
        'border border-solid border-red-600',
        'has-[:focus-visible]:border-2 has-[:focus-visible]:border-red-600',
        'dark:border-red-500 dark:has-[:focus-visible]:border-red-500',
      ].join(' ');
    }
    if (state === 'success') {
      return [
        'border border-solid border-emerald-600',
        'has-[:focus-visible]:border-2 has-[:focus-visible]:border-emerald-600',
        'dark:border-emerald-500 dark:has-[:focus-visible]:border-emerald-500',
      ].join(' ');
    }
    if (state === 'alert') {
      return [
        'border border-solid border-amber-600',
        'has-[:focus-visible]:border-2 has-[:focus-visible]:border-amber-600',
        'dark:border-amber-500 dark:has-[:focus-visible]:border-amber-500',
      ].join(' ');
    }
    return [
      'border border-solid border-edge',
      'hover:border-edge-strong dark:hover:border-gray-500',
      'has-[:focus-visible]:border-2 has-[:focus-visible]:border-content-secondary',
      'dark:has-[:focus-visible]:border-gray-400',
    ].join(' ');
  })();

  return (
    <div className={['relative z-0 flex w-full flex-col box-border transition-[border-color,border-width] duration-150', className].filter(Boolean).join(' ')}>
      {label ? (
        <label
          htmlFor={inputId}
          className={[
            'absolute left-[15px] top-[-8px] z-[1] bg-surface px-1 font-bold',
            labelTone,
          ].join(' ')}
        >
          <span className={labelSizeClass}>{label}</span>
        </label>
      ) : null}

      <div
        className={['flex items-center gap-1 rounded-[8px] border border-solid px-4 py-1 box-border', heightClass, wrapperBorderClasses].join(' ')}
      >
        {LeftIcon ? (
          <span
            className={[
              'inline-flex shrink-0',
              disabled ? 'text-content-faint' : 'text-content-secondary dark:text-content-muted',
            ].join(' ')}
            aria-hidden
          >
            <LeftIcon size={iconSize} strokeWidth={2} />
          </span>
        ) : null}
        <input
          {...restInput}
          id={inputId}
          type={inputType ?? 'text'}
          value={displayValue}
          placeholder={placeholder}
          disabled={disabled}
          onChange={handleChange}
          aria-invalid={state === 'error' && !disabled}
          aria-describedby={describedBy}
          className={[
            'min-h-0 min-w-0 flex-1 border-0 bg-transparent font-normal outline-none',
            'text-content placeholder:text-content-faint',
            'disabled:text-content-faint',
            inputSizeClass,
            disabled ? 'cursor-not-allowed' : '',
            inputClassName,
          ]
            .filter(Boolean)
            .join(' ')}
          style={inputStyle}
        />
        {RightIcon ? (
          <span
            className={[
              'inline-flex shrink-0',
              disabled ? 'text-content-faint' : 'text-content-secondary dark:text-content-muted',
            ].join(' ')}
            aria-hidden
          >
            <RightIcon size={iconSize} strokeWidth={2} />
          </span>
        ) : null}
      </div>

      {supportingText ? (
        <p
          id={supportId}
          className={['m-0 px-5 py-1 text-[12px] font-normal leading-5 tracking-normal', supportingTone].join(' ')}
        >
          {supportingText}
        </p>
      ) : null}
    </div>
  );
};

export default Textfield;
